import { useState, useEffect } from 'react';
import { BEHAVIOURAL_PATTERN_CHIPS, CLIENT_PRIORITY_CHIPS, HelpTip, IntelListEditor, Loading, PANEL_HELP, api, useToast, PanelHowTo } from './_barrel.js';


        function IntelPacketPanel({ token, subjectId, subject }) {
            const [packet, setPacket] = useState(null);
            const [vocabs, setVocabs] = useState(null);
            const [loading, setLoading] = useState(true);
            const [saving, setSaving] = useState(false);
            const [error, setError] = useState(null);
            const [expanded, setExpanded] = useState(false);
            const [dirty, setDirty] = useState(false);
            const toast = useToast();

            useEffect(() => {
                (async () => {
                    setLoading(true);
                    try {
                        const [p, v] = await Promise.all([
                            api(`/api/v1/subjects/${subjectId}/intel-packet`, {}, token),
                            api(`/api/v1/vocabularies`, {}, token),
                        ]);
                        setPacket(p);
                        setVocabs(v);
                    } catch (e) { setError(e.message); }
                    finally { setLoading(false); }
                })();
            }, [token, subjectId]);

            const updateField = (key, value) => {
                setPacket(p => ({ ...p, [key]: value }));
                setDirty(true);
            };

            const save = async () => {
                if (!packet) return;
                setSaving(true);
                setError(null);
                try {
                    const body = {
                        threat_actors: packet.threat_actors || [],
                        historic_incidents: packet.historic_incidents || [],
                        close_associates: packet.close_associates || [],
                        behavioural_patterns: packet.behavioural_patterns || [],
                        client_priorities: packet.client_priorities || [],
                        additional_context: packet.additional_context || null,
                    };
                    const r = await api(`/api/v1/subjects/${subjectId}/intel-packet`,
                        { method: 'PUT', body: JSON.stringify(body) }, token);
                    setPacket(r);
                    setDirty(false);
                    toast('Intelligence packet saved');
                } catch (e) { setError(e.message); }
                finally { setSaving(false); }
            };

            const totalCount = packet ? (
                (packet.threat_actors?.length || 0) +
                (packet.historic_incidents?.length || 0) +
                (packet.close_associates?.length || 0) +
                (packet.behavioural_patterns?.length || 0) +
                (packet.client_priorities?.length || 0) +
                (packet.additional_context ? 1 : 0)
            ) : 0;

            return (
                <div className="panel mb-6">
                    <div className="panel-header is-self-collapse flex justify-between items-center cursor-pointer"
                         onClick={() => setExpanded(!expanded)}>
                        <div>
                            <div className="font-display text-xl gold">Intelligence Packet<HelpTip text={PANEL_HELP.intelligence_packet} /></div>
                            <div className="text-xs text-muted font-mono mt-1">
                                {loading ? 'Loading…' :
                                 totalCount === 0 ? 'Empty — add context to make every assessment subject-specific' :
                                 `${totalCount} item(s) curated · feeds every LLM call about this subject`}
                                {packet?.last_edited_by && ` · last edited by ${packet.last_edited_by}`}
                            </div>
                        </div>
                        <div className="text-xs text-muted">{expanded ? '▾' : '▸'}</div>
                    </div>
                    <PanelHowTo id="intel" />

                    {expanded && (
                        <div className="px-6 py-5 space-y-6">
                            {error && <div className="text-[var(--red)] text-sm">{error}</div>}

                            {!packet ? (
                                <div className="text-muted text-sm">Loading…</div>
                            ) : (
                                <>
                                    <IntelListEditor
                                        title="Known Threat Actors"
                                        helpText="Named individuals or groups known to threaten this subject. Free text; will become structured entities later."
                                        items={packet.threat_actors || []}
                                        setItems={(v) => updateField('threat_actors', v)}
                                        fields={[
                                            { key: 'name', label: 'Name', placeholder: 'e.g. @username or Real Name', required: true },
                                            { key: 'relationship', label: 'Relationship',
                                                type: 'dropdown',
                                                options: vocabs?.threat_actor_relationships || [] },
                                            { key: 'first_observed', label: 'First observed', placeholder: 'e.g. Mar 2023' },
                                            { key: 'notes', label: 'Notes', placeholder: 'context, severity, history', multiline: true },
                                        ]}
                                    />

                                    <IntelListEditor
                                        title="Historic Incidents Worth Knowing"
                                        helpText="Things that happened before Veil started monitoring — assaults, threats received offline, court orders, etc."
                                        items={packet.historic_incidents || []}
                                        setItems={(v) => updateField('historic_incidents', v)}
                                        fields={[
                                            { key: 'description', label: 'Description', placeholder: 'what happened', required: true },
                                            { key: 'incident_type', label: 'Type',
                                                type: 'dropdown',
                                                options: vocabs?.historic_incident_types || [] },
                                            { key: 'when_approx', label: 'When (approx)', placeholder: 'e.g. summer 2022' },
                                            { key: 'notes', label: 'Notes', placeholder: 'outcome, ongoing risk', multiline: true },
                                        ]}
                                    />

                                    <IntelListEditor
                                        title="Close Associates"
                                        helpText="First name + relationship only. Do not record surnames, DOBs, addresses, or contact details — minimal third-party data only."
                                        items={packet.close_associates || []}
                                        setItems={(v) => updateField('close_associates', v)}
                                        fields={[
                                            { key: 'first_name', label: 'First name', placeholder: 'first name only', required: true },
                                            { key: 'relationship', label: 'Relationship',
                                                type: 'dropdown',
                                                options: vocabs?.close_associate_relationships || [],
                                                required: true },
                                        ]}
                                    />

                                    <IntelListEditor
                                        title="Behavioural Patterns"
                                        helpText="What this subject does that affects their threat picture — over-sharing on social media, public schedule, etc."
                                        items={packet.behavioural_patterns || []}
                                        setItems={(v) => updateField('behavioural_patterns', v)}
                                        chips={BEHAVIOURAL_PATTERN_CHIPS}
                                        chipFieldKey="description"
                                        chipHint="Click a chip to start, then edit to be specific to this subject."
                                        fields={[
                                            { key: 'description', label: 'Pattern', placeholder: 'e.g. posts location in real time', required: true, multiline: true },
                                        ]}
                                    />

                                    <IntelListEditor
                                        title="Client Priorities"
                                        helpText="What the client specifically cares about — family safety, court appearance dates, business deals in flight."
                                        items={packet.client_priorities || []}
                                        setItems={(v) => updateField('client_priorities', v)}
                                        chips={CLIENT_PRIORITY_CHIPS}
                                        chipFieldKey="description"
                                        chipHint="Click a chip to start, then edit to be specific to this subject."
                                        fields={[
                                            { key: 'description', label: 'Priority', placeholder: 'e.g. testifying at trial September 2026', required: true, multiline: true },
                                        ]}
                                    />

                                    <div>
                                        <label>Additional Context (Free Text)</label>
                                        <textarea rows="5" value={packet.additional_context || ''}
                                            onChange={e => updateField('additional_context', e.target.value)}
                                            placeholder="Anything else the LLM should know about this subject that doesn't fit the structured fields above."></textarea>
                                        <div className="text-xs text-muted mt-1">
                                            This text is read by every LLM call. Keep it factual and current.
                                        </div>
                                    </div>

                                    <div className="flex justify-end items-center gap-3 pt-2 border-t border-veil">
                                        {dirty && <span className="text-xs text-muted font-mono">unsaved changes</span>}
                                        <button type="button" className="btn-gold" onClick={save} disabled={saving || !dirty}>
                                            {saving ? 'Saving…' : 'Save Packet'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            );
        }

export { IntelPacketPanel };
